import { buildCompanySelection, buildSearchRequestBody } from "@/utils/searchRequestBuilder.util";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { SelectOptionRequestDto } from "@advanced-sales-technologies/backend-client/models/SelectOptionRequestDto";
import type { SimpleSelectOptionDto } from "@advanced-sales-technologies/backend-client/models/SimpleSelectOptionDto";
import { useIsMutating } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import clsx from "clsx";
import { HiOutlineEnvelope } from "react-icons/hi2";

import { useGetContactPersonEmailOptions } from "@/hooks/api/list/options/useGetContactPersonEmailOptions";
import { useGetExternalContactPersonEmailOptions } from "@/hooks/api/list/options/useGetExternalContactPersonEmailOptions";
import {
  externalPersonEmailMutationKey,
  useGetExternalPersonEmails,
} from "@/hooks/api/list/options/useGetExternalPersonEmails";
import { personEmailMutationKey, useGetPersonEmails } from "@/hooks/api/list/options/useGetPersonEmails";
import Button from "@/components/atoms/Button";
import Spinner from "@/components/atoms/Spinner";
import BaseColumn from "@/components/molecules/table/BaseColumn";
import HasMoreDataCellGeneral from "@/components/molecules/table/HasMoreDataCellGeneral";
import { searchColumns, SearchColumnsProps } from "@/components/organisms/search/searchColumns";

const personEmailColumn = ({ t, listId, searchParams, queryKey }: SearchColumnsProps): ColumnDef<CompanyEntryDto> =>
  BaseColumn<CompanyEntryDto>({
    accessorFn: (row) => row.point_of_contact?.contact_info?.email?.value,
    id: searchColumns(t).point_of_contact_email.id,
    header: () => searchColumns(t).point_of_contact_email.name,
    cell: ({ row }) => {
      const company = row.original;
      const { id: companyId } = company;
      const { getExternalEmails } = useGetExternalPersonEmails(companyId);
      const { getEmails } = useGetPersonEmails(companyId);

      const isLoadingExternalEmails = useIsMutating({ mutationKey: externalPersonEmailMutationKey(companyId) });
      const isLoadingEmails = useIsMutating({ mutationKey: personEmailMutationKey(companyId) });
      const contact = company.point_of_contact;

      if (isLoadingEmails || isLoadingExternalEmails)
        return (
          <div className={"flex items-center gap-2"}>
            <Spinner size={"2xs"} />
            <span className={"text-sm"}>{t("search.table.contact.loading")}</span>
          </div>
        );

      if (
        !contact ||
        !contact.contact_info ||
        (!contact.contact_info?.email && contact.contact_info.external_email_not_found)
      )
        return <span className={"text-quinary"}>{t("nothingFound")}</span>;

      const fetchEmailsHook = contact.is_external ? getExternalEmails : getEmails;
      const queryHook = contact.is_external ? useGetExternalContactPersonEmailOptions : useGetContactPersonEmailOptions;

      if (contact.contact_info.email && contact.contact_info.email.value) {
        return (
          <HasMoreDataCellGeneral<SimpleSelectOptionDto>
            queryKey={queryKey}
            company={company}
            buildDisplayValue={(option) => option.value || ""}
            searchParams={searchParams}
            displayValue={contact.contact_info.email.value}
            initialSelected={contact.contact_info.email.id}
            listId={listId}
            target={
              contact.is_external
                ? SelectOptionRequestDto.target.EXTERNAL_CONTACT_PERSON_EMAIL
                : SelectOptionRequestDto.target.CONTACT_PERSON_EMAIL
            }
            hasMoreData={contact.contact_info.email.has_more}
            placeholder={searchColumns(t).point_of_contact_email.name}
            useQueryHook={(query, enabled, searchParams) =>
              queryHook(
                {
                  id: contact.id,
                  companyId: companyId,
                  option: {
                    query: query,
                    list_id: listId,
                    configuration: searchParams,
                  },
                },
                enabled,
              )
            }
            renderValue={(value) => (
              <a href={`mailto:${value}`} target="_blank" rel="noreferrer" className={"text-link-gray underline"}>
                {value}
              </a>
            )}
            renderOption={(option, isSelected) => (
              <span
                key={option.id}
                className={"flex flex-1 flex-col gap-x-1.5 truncate py-2 text-left text-sm text-quaternary"}
              >
                {option.value && (
                  <div className={clsx("truncate font-medium", isSelected ? "text-primary" : "text-secondary")}>
                    {option.value}
                  </div>
                )}
              </span>
            )}
          />
        );
      } else {
        return (
          <div>
            <Button
              size={"3xs"}
              variants={"outline"}
              color={"natural"}
              leadingIcon={<HiOutlineEnvelope className={"size-4"} />}
              label={t("search.table.contact.getEmails")}
              onClick={() =>
                fetchEmailsHook({
                  id: contact.id,
                  companyId: companyId,
                  option: {
                    list_id: listId,
                    configuration: searchParams ? buildSearchRequestBody(searchParams) : {},
                    selections: buildCompanySelection(company),
                  },
                  queryKey,
                })
              }
            />
          </div>
        );
      }
    },
    meta: {
      heading: {
        icon: HiOutlineEnvelope,
        className: "!bg-var-secondary-50 hover:!bg-var-secondary-100 active:!bg-var-secondary-200",
      },
      column: {
        className: "bg-var-secondary-25",
      },
      disableTooltip: true,
    },
  });

export default personEmailColumn;
